import { forwardRef, isValidElement } from 'react';
import { useIntl } from 'react-intl';

import { DotMenuIcon } from '@dynatrace/strato-icons';

import { IntentButtonWithoutResponse } from './IntentButtonWithoutResponse.js';
import { IntentButtonWithResponse } from './IntentButtonWithResponse.js';
import { OpenWith } from './OpenWith.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import {
  Intent as Item,
  type IntentProps,
} from '../../core/slots/Intent/Intent.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { Menu } from '../../navigation/menu/Menu.js';
import { Prefix as IconPrefix } from '../../navigation/menu/Prefix.js';
import { Button, ButtonOwnProps } from '../button/Button.js';

const COMPONENT_NAME = 'IntentButton';

//#region Typings

/**
 * Accepted properties for the IntentButton.
 * @public
 */
export type IntentButtonProps = Exclude<IntentProps, 'onSelect' | 'icon'> &
  Pick<
    ButtonOwnProps,
    | 'onClick'
    | 'color'
    | 'variant'
    | 'width'
    | 'aria-disabled'
    | 'textAlign'
    | 'size'
    | 'disabled'
  > &
  BehaviorTrackingProps & {
    /**
     * Enable the three dot menu for additional intents.
     * This menu includes a fallback "Open with..." entry for cases where specific intents are unavailable.
     * @defaultValue true
     */
    showMenu?: boolean;
  };

//#endregion

const _IntentButton = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  IntentButtonProps
>((props, forwardedRef) => {
  const {
    payload,
    children,
    onResponse,
    options,
    disabled,
    color,
    variant = 'emphasized',
    width,
    onClick,
    size,
    textAlign,
    showMenu = true,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: IntentButtonProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const [slots, freeform] = useSlots(children, {
    icon: IconPrefix,
    items: { match: Item, multiple: true },
    openWith: OpenWith,
  });

  const slottedIcon = isValidElement(slots.icon) ? slots.icon : undefined;

  const intl = useIntl();
  const menuAriaLabel = intl.formatMessage({
    defaultMessage: 'Open intent menu',
    id: 'OEhnk3JokTy0c/JX',
    description: 'Aria label for the IntentButton menu trigger.',
  });

  const sharedProps: ButtonOwnProps = {
    color,
    variant,
    width,
    size,
    textAlign,
    onClick,
    disabled,
    'aria-disabled': ariaDisabledProp,
  };

  return (
    <InputGroup
      ref={forwardedRef}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      className={consumerClassName}
      style={consumerStyle}
      {...mergeProps(restProps, behavioralTrackingProps)}
      data-dt-component={COMPONENT_NAME}
    >
      {'responseProperties' in options ? (
        <IntentButtonWithResponse
          {...sharedProps}
          payload={payload}
          options={options}
          onResponse={onResponse ?? (() => undefined)}
          icon={slottedIcon}
        >
          {freeform}
        </IntentButtonWithResponse>
      ) : (
        <IntentButtonWithoutResponse
          {...sharedProps}
          payload={payload}
          options={options}
          icon={slottedIcon}
        >
          {freeform}
        </IntentButtonWithoutResponse>
      )}
      {showMenu && (
        <Menu>
          <Menu.Trigger aria-label={menuAriaLabel}>
            <Button
              disabled={disabled}
              aria-disabled={ariaDisabledProp}
              variant={variant}
              color={color}
              size={size}
              style={{ width: 'max-content', minWidth: 'fit-content' }}
            >
              <DotMenuIcon />
            </Button>
          </Menu.Trigger>
          <Menu.Content
            alignment="end"
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
          >
            {slots.items && (
              <Menu.Group>
                {slots.items.map((item, index) => (
                  <Menu.Intent
                    {...item.props}
                    key={`${item.props.options.recommendedAppId}-${index}`}
                  />
                ))}
              </Menu.Group>
            )}
            <Menu.Group>
              {slots.openWith ?? <OpenWith payload={payload} />}
            </Menu.Group>
          </Menu.Content>
        </Menu>
      )}
    </InputGroup>
  );
});

(_IntentButton as typeof _IntentButton & { displayName: string }).displayName =
  COMPONENT_NAME;

/**
 * The `IntentButton` is a dedicated component for sending
 * {@link https://developer.dynatrace.com/develop/guides/navigation/intents/ | intents}.
 * The `IntentButton` only works within the AppShell context.
 * @public
 */
export const IntentButton = Object.assign(_IntentButton, {
  Item,
  OpenWith,
  Icon: IconPrefix,
});
